import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Target, Heart, Users, Lightbulb } from 'lucide-react';

const AboutPage = () => {
  const values = [
    { icon: Target, title: 'Excellence', description: 'We strive for excellence in every order we fulfill.' },
    { icon: Heart, title: 'Care', description: 'We treat every package as if it were our own.' },
    { icon: Users, title: 'Partnership', description: 'We see ourselves as an extension of your team.' },
    { icon: Lightbulb, title: 'Innovation', description: 'We continuously improve to serve you better.' },
  ];

  const milestones = [
    { year: '2019', title: 'Founded', desc: 'Started in Phoenix, AZ' },
    { year: '2020', title: 'POD Launch', desc: 'Added print services' },
    { year: '2021', title: 'Global Reach', desc: 'International expansion' },
    { year: '2022', title: 'Tech Upgrade', desc: 'Advanced systems' },
    { year: '2023', title: 'TikTok Partner', desc: 'Social commerce' },
    { year: '2024', title: '10M Orders', desc: 'Major milestone' },
  ];

  return (
    <div data-testid="about-page" className="bg-[#fffdf8]">
      {/* Hero */}
      <section className="pt-32 pb-20 warm-gradient relative overflow-hidden">
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#ea580c]/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#ea580c] text-sm font-medium tracking-wider uppercase mb-4 block">
                About Us
              </span>
              <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-6xl font-semibold text-[#78350f] mb-6" data-testid="about-hero-title">
                Granite Current
                <br />
                <span className="text-[#ea580c]">LLC</span>
              </h1>
              <p className="text-[#78350f]/80 text-lg mb-8 leading-relaxed">
                We are a full-service ecommerce fulfillment company dedicated to helping 
                online businesses grow naturally. Based in Phoenix, Arizona, 
                we provide comprehensive Print on Demand, Dropshipping, and Warehousing 
                solutions to sellers worldwide.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/services"
                  data-testid="about-cta-services"
                  className="bg-[#ea580c] text-white px-6 py-3 font-medium hover:bg-[#c2410c] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg inline-flex items-center gap-2 group organic-shape-3"
                >
                  Our Services
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="about-cta-contact"
                  className="bg-transparent text-[#78350f] px-6 py-3 font-medium border-2 border-[#78350f] hover:bg-[#78350f] hover:text-white transition-all duration-300 organic-shape-3"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="organic-shape-1 overflow-hidden warm-shadow-lg">
                <img
                  src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Our team"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#78350f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '2019', label: 'Founded' },
              { value: '50+', label: 'Team Members' },
              { value: '100K+', label: 'Products' },
              { value: '5,000+', label: 'Happy Clients' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`about-stat-${index}`}
              >
                <div className="font-['Cormorant_Garamond'] text-4xl font-semibold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-[#fcd9b8] text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-[#fffdf8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#fef3c7]/50 p-10 organic-shape-2"
            >
              <div className="w-14 h-14 bg-[#ea580c] rounded-2xl flex items-center justify-center mb-6">
                <Target size={28} className="text-white" />
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#78350f] mb-4">
                Our Mission
              </h2>
              <p className="text-[#78350f]/80 leading-relaxed">
                To empower ecommerce entrepreneurs by providing reliable, scalable, 
                and cost-effective fulfillment solutions that enable them to focus 
                on growing their businesses naturally.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-[#fef3c7]/50 p-10 organic-shape-1"
            >
              <div className="w-14 h-14 bg-[#78350f] rounded-2xl flex items-center justify-center mb-6">
                <Lightbulb size={28} className="text-white" />
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#78350f] mb-4">
                Our Vision
              </h2>
              <p className="text-[#78350f]/80 leading-relaxed">
                To become the most trusted fulfillment partner for ecommerce businesses 
                worldwide, known for our warm service, excellence, and innovation.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#fef3c7]/30" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#ea580c] text-sm font-medium tracking-wider uppercase mb-4 block">
              What Drives Us
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-semibold text-[#78350f]">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 text-center hover:translate-y-[-4px] hover:shadow-xl transition-all duration-300 organic-shape-2"
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#ea580c] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon size={26} className="text-white" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#78350f] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#78350f]/70 text-sm">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-[#fffdf8]" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#ea580c] text-sm font-medium tracking-wider uppercase mb-4 block">
              Our Journey
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-semibold text-[#78350f]">
              Milestones
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#fef3c7]/50 p-6 text-center organic-shape-3"
                data-testid={`milestone-${index}`}
              >
                <span className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#ea580c]">{milestone.year}</span>
                <h4 className="font-semibold text-[#78350f] mt-2">{milestone.title}</h4>
                <p className="text-[#78350f]/60 text-xs mt-1">{milestone.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-24 bg-[#fef3c7]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#ea580c] text-sm font-medium tracking-wider uppercase mb-4 block">
                Why Work With Us
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-semibold text-[#78350f] mb-6">
                Your Success Is
                <br />
                <span className="text-[#ea580c]">Our Priority</span>
              </h2>
              <p className="text-[#78350f]/80 text-lg mb-8 leading-relaxed">
                We don't just fulfill orders – we nurture partnerships. 
                Our dedicated team and warm approach make us the ideal 
                fulfillment partner for growing businesses.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Dedicated account manager',
                  'Advanced technology integration',
                  'Flexible solutions that scale',
                  'Transparent pricing',
                  'Industry-leading accuracy'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#fef3c7] rounded-full flex items-center justify-center">
                      <Check size={14} className="text-[#ea580c]" />
                    </div>
                    <span className="text-[#78350f]">{item}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                data-testid="about-cta-bottom"
                className="inline-flex items-center gap-2 bg-[#ea580c] text-white px-6 py-3 font-medium hover:bg-[#c2410c] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-lg group organic-shape-3"
              >
                Partner With Us
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="organic-shape-2 overflow-hidden warm-shadow-lg">
                <img
                  src="https://images.pexels.com/photos/4483608/pexels-photo-4483608.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Warehouse team"
                  className="w-full aspect-square object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#78350f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-semibold text-white mb-6">
              Ready to Start Your{' '}
              <span className="text-[#fef3c7]">Journey?</span>
            </h2>
            <p className="text-[#fcd9b8] text-lg mb-10">
              Join thousands of successful sellers who trust Granite Current 
              for their fulfillment needs.
            </p>
            <Link
              to="/contact"
              data-testid="about-final-cta"
              className="inline-flex items-center gap-2 bg-[#ea580c] text-white px-8 py-4 font-medium hover:bg-[#c2410c] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-xl group organic-shape-3"
            >
              Get in Touch
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
